"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OVERLAY_MIN_TEXT_FONT_SIZE = exports.OVERLAY_LINE_HEIGHT_RATIO = exports.TEXT_OVERLAY_BORDER = exports.TEXT_OVERLAY_PAD = exports.OVERLAY_PADDING = exports.OVERLAY_FONT_SIZE = void 0;
exports.wrapOverlayLines = wrapOverlayLines;
exports.layoutOverlayText = layoutOverlayText;
/** Live overlay CSS px and baked PDF pt stay in lockstep. */
exports.OVERLAY_FONT_SIZE = 14;
/** Signature / date stamp inset. Text stamps use {@link TEXT_OVERLAY_PAD}. */
exports.OVERLAY_PADDING = 4;
/**
 * Inner padding of the live wrapping textarea and the archival text stamp.
 * Smaller than {@link OVERLAY_PADDING} so a 14px two-line drop still has room.
 */
exports.TEXT_OVERLAY_PAD = 2;
/** Outline of the live textarea and the archival text stamp. */
exports.TEXT_OVERLAY_BORDER = 2;
/** Line box as a multiple of {@link OVERLAY_FONT_SIZE} — textarea wrap and the baker share it. */
exports.OVERLAY_LINE_HEIGHT_RATIO = 1.2;
/** Smallest size the live textarea shrinks to before it refuses more text; the stamp never goes below it. */
exports.OVERLAY_MIN_TEXT_FONT_SIZE = exports.OVERLAY_FONT_SIZE * 0.8;
/** Each shrink step, in pt. The live textarea steps by the same amount times the render scale. */
const OVERLAY_FONT_STEP = 0.4;
const OVERLAY_SHRINK_STEPS = Math.round((exports.OVERLAY_FONT_SIZE - exports.OVERLAY_MIN_TEXT_FONT_SIZE) / OVERLAY_FONT_STEP);
/** Ends the last stamped line when the value has more than the box shows. */
const OVERLAY_TRUNCATION_MARK = '\u2026';
/**
 * Word-wrap `text` to `maxWidth` using `widthOf` (pdf-lib `widthOfTextAtSize`,
 * or any linear stand-in), the way the live `white-space: pre-wrap` textarea does: spaces are kept
 * as typed, a line breaks at a space (never a no-break space) or after hyphens that run into more
 * text, and the spaces at a break hang off the end of the line instead of starting the next one.
 * Hard-breaks a token that is wider than the box so the stamp never grows sideways over the page. Each line break (LF, CRLF or CR) starts
 * a new line, as it does in the live textarea. Empty input yields no lines.
 */
function wrapOverlayLines(text, maxWidth, widthOf) {
    var _a, _b;
    if (!text) {
        return [];
    }
    if (maxWidth <= 0) {
        return [text];
    }
    const lines = [];
    for (const paragraph of text.split(/\r\n?|\n/)) {
        if (paragraph === '') {
            lines.push('');
            continue;
        }
        let current = '';
        let wrapped = false;
        for (const token of (_a = paragraph.match(/ +|[^ ]*?[-/\u2013\u2014]+(?=[^ \-/\u2013\u2014])|[^ ]+/g)) !== null && _a !== void 0 ? _a : []) {
            if (token.startsWith(' ')) {
                // Spaces never wrap: they stay on this line, and are dropped at the start of a wrapped one.
                if (current || !wrapped) {
                    current += token;
                }
                continue;
            }
            const next = current + token;
            if (widthOf(next) <= maxWidth) {
                current = next;
                continue;
            }
            wrapped = true;
            if (current.trim()) {
                lines.push(current.trimEnd());
            }
            const broken = breakLongToken(token, maxWidth, widthOf);
            current = (_b = broken.pop()) !== null && _b !== void 0 ? _b : '';
            lines.push(...broken);
        }
        if (current) {
            lines.push(current);
        }
    }
    return lines;
}
function breakLongToken(token, maxWidth, widthOf) {
    if (widthOf(token) <= maxWidth) {
        return [token];
    }
    const parts = [];
    let current = '';
    for (const ch of token) {
        const next = current + ch;
        if (current && widthOf(next) > maxWidth) {
            parts.push(current);
            current = ch;
        }
        else {
            current = next;
        }
    }
    if (current) {
        parts.push(current);
    }
    return parts;
}
/**
 * Line breaks and a draw size that fit inside `boxWidth` × `boxHeight`.
 * `widthOfAt14` is pdf-lib `widthOfTextAtSize` at {@link OVERLAY_FONT_SIZE}.
 *
 * The live overlay is 14px CSS Helvetica/Arial in a border-box textarea — not
 * 14pt WinAnsi Helvetica — so wrapping at 14pt first, then shrinking those
 * lines, paints an extra line the signer never saw. Cap at the number of 14px
 * line-boxes the authored height can hold, then step down from 14pt the way the live field does
 * and take the first size whose wrap fits, so a two-line field stays two lines.
 *
 * Never below {@link OVERLAY_MIN_TEXT_FONT_SIZE}, where the live field stops taking text. A value
 * that still does not fit (one written straight to Cortex) keeps the lines the box holds and ends
 * the last with an ellipsis, so the stamp stays legible instead of shrinking to nothing.
 */
function layoutOverlayText(text, boxWidth, boxHeight, widthOfAt14) {
    var _a;
    const inset = exports.TEXT_OVERLAY_PAD * 2 + exports.TEXT_OVERLAY_BORDER * 2;
    const usableW = Math.max(0, boxWidth - inset);
    const usableH = Math.max(0, boxHeight - inset);
    const widthAt = (s, size) => widthOfAt14(s) * (size / exports.OVERLAY_FONT_SIZE);
    const layoutAt = (size) => {
        const lines = wrapOverlayLines(text, usableW, (s) => widthAt(s, size));
        return { lines, fontSize: size, lineHeight: size * exports.OVERLAY_LINE_HEIGHT_RATIO };
    };
    const lineBox14 = exports.OVERLAY_FONT_SIZE * exports.OVERLAY_LINE_HEIGHT_RATIO;
    const maxLines = Math.max(1, Math.ceil(usableH / lineBox14 - 1e-6));
    const fits = (size) => {
        const layout = layoutAt(size);
        if (layout.lines.length === 0) {
            return layout;
        }
        if (layout.lines.length > maxLines) {
            return null;
        }
        if (layout.lines.length * layout.lineHeight > usableH + 0.01) {
            return null;
        }
        return layout;
    };
    for (let step = 0; step <= OVERLAY_SHRINK_STEPS; step += 1) {
        const layout = fits(step === OVERLAY_SHRINK_STEPS ? exports.OVERLAY_MIN_TEXT_FONT_SIZE : exports.OVERLAY_FONT_SIZE - step * OVERLAY_FONT_STEP);
        if (layout) {
            return layout;
        }
    }
    const layout = layoutAt(exports.OVERLAY_MIN_TEXT_FONT_SIZE);
    if (layout.lines.length <= maxLines) {
        return layout;
    }
    const lines = layout.lines.slice(0, maxLines);
    let last = (_a = lines[maxLines - 1]) !== null && _a !== void 0 ? _a : '';
    const lastFits = (candidate) => widthAt(candidate + OVERLAY_TRUNCATION_MARK, exports.OVERLAY_MIN_TEXT_FONT_SIZE) <= usableW;
    while (last && !lastFits(last)) {
        last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last.trimEnd() + OVERLAY_TRUNCATION_MARK;
    return Object.assign(Object.assign({}, layout), { lines });
}
//# sourceMappingURL=overlay_text_metrics.js.map